import { useState } from 'react';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, Lock, KeyRound } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

type Step = 'request' | 'confirm' | 'done';

export function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await resetPassword({ username: email });
      setStep('confirm');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('User does not exist') || msg.includes('Username/client id combination not found')) {
        // Don't reveal whether the account exists
        setStep('confirm');
      } else if (msg.includes('LimitExceededException') || msg.includes('Attempt limit exceeded')) {
        setError('Too many attempts. Please wait a few minutes before trying again.');
      } else {
        setError(`Could not send reset code: ${msg}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      setStep('done');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('Invalid verification code') || msg.includes('CodeMismatchException')) {
        setError('That code doesn\'t match. Please check and try again.');
      } else if (msg.includes('ExpiredCodeException') || msg.includes('expired')) {
        setError('The code has expired. Go back and request a new one.');
      } else if (msg.includes('Password does not conform') || msg.includes('InvalidPasswordException')) {
        setError('Password must include upper- and lower-case letters, a number, and be at least 8 characters.');
      } else {
        setError(`Could not reset password: ${msg}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'done') {
    return (
      <Card className="w-full max-w-md p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="h-12 w-12 rounded-xl bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 flex items-center justify-center">
            <KeyRound className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Password updated</h1>
        <p className="text-muted-foreground mt-2 mb-6">
          Your password has been reset. You can now sign in with your new password.
        </p>
        <Button className="w-full" onClick={() => navigate('/login')}>
          Back to sign in
        </Button>
      </Card>
    );
  }

  if (step === 'confirm') {
    return (
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center">
              <KeyRound className="h-6 w-6 text-primary-strong" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Check your email</h1>
          <p className="text-muted-foreground mt-2">
            We sent a reset code to{' '}
            <span className="font-medium text-gray-700 dark:text-foreground">{email}</span>.
            Enter it below along with your new password.
          </p>
        </div>

        <form onSubmit={handleConfirm} className="space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-500 text-red-700 dark:text-red-400 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="code">Reset code</Label>
            <Input
              id="code"
              type="text"
              placeholder="Enter 6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="text-center text-lg tracking-widest"
              maxLength={6}
              required
              autoFocus
              autoComplete="one-time-code"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">New password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="newPassword"
                type="password"
                placeholder="At least 8 characters"
                className="pl-10"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Must include upper- and lower-case letters and a number.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Re-enter new password"
                className="pl-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Resetting...
              </>
            ) : (
              'Reset password'
            )}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Didn&apos;t get the code?{' '}
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 font-medium"
            onClick={() => setStep('request')}
          >
            Try again
          </button>
        </p>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md p-8">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="h-12 w-12 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center">
            <Mail className="h-6 w-6 text-primary-strong" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Forgot your password?</h1>
        <p className="text-muted-foreground mt-2">
          Enter your email address and we&apos;ll send you a reset code.
        </p>
      </div>

      <form onSubmit={handleRequest} className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-500 text-red-700 dark:text-red-400 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              className="pl-10"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
            />
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sending code...
            </>
          ) : (
            'Send reset code'
          )}
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Remember your password?{' '}
        <Link to="/login" className="text-blue-600 hover:text-blue-800 font-medium">
          Sign in
        </Link>
      </p>
    </Card>
  );
}
